<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import AiAgentCard from './AiAgentCard.vue';
import AiAgentsCreateCard from './AiAgentsCreateCard.vue';

const router = useRouter();
const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const aiAgents = useMapGetter('aiAgents/getAgents');
const uiFlags = useMapGetter('aiAgents/getUIFlags');
const searchQuery = ref('');
const selectedAgent = ref({});
const deleteDialogRef = ref(null);
const togglingAgentIds = ref({});

const filteredAiAgents = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return aiAgents.value;

  return picoSearch(aiAgents.value, query, ['name', 'description', 'model']);
});

const isAgentActive = agent => agent.status === 'published' && agent.is_active;

const buildDuplicatePayload = agent => ({
  emoji: agent.emoji,
  name: t('AI_AGENTS.CARD.DUPLICATE_NAME', { name: agent.name }),
  description: agent.description,
  instructions: agent.instructions,
  provider: agent.provider,
  model: agent.model,
  temperature: agent.temperature,
  max_tokens: agent.max_tokens,
  actions: (agent.actions || []).map(action => ({
    action_type: action.action_type,
    is_enabled: action.is_enabled,
    instructions: action.instructions,
    config: action.config || {},
  })),
});

const openCreate = () => {
  router.push({ name: 'ai_agents_templates' });
};

const openKnowledgeSettings = () => {
  router.push(
    accountScopedRoute('ai_knowledge_sources_settings', {}, { add: '1' })
  );
};

const openEdit = agent => {
  router.push({ name: 'ai_agents_edit', params: { id: agent.id } });
};

const openDeleteDialog = agent => {
  selectedAgent.value = agent;
  deleteDialogRef.value.open();
};

const deleteAgent = async () => {
  await store.dispatch('aiAgents/delete', selectedAgent.value.id);
  useAlert(t('AI_AGENTS.API.DELETE_SUCCESS'));
  selectedAgent.value = {};
};

const duplicateAgent = async agent => {
  const duplicated = await store.dispatch(
    'aiAgents/create',
    buildDuplicatePayload(agent)
  );
  if (!duplicated) return;

  useAlert(t('AI_AGENTS.API.DUPLICATE_SUCCESS'));
};

const toggleAgentActive = async ({ agent, isActive }) => {
  if (isActive === isAgentActive(agent)) return;

  togglingAgentIds.value[agent.id] = true;

  try {
    if (isActive) {
      if (agent.status !== 'published') {
        await store.dispatch('aiAgents/publish', agent.id);
      } else {
        await store.dispatch('aiAgents/update', {
          id: agent.id,
          data: { is_active: true },
        });
      }
      useAlert(t('AI_AGENTS.API.ACTIVATE_SUCCESS'));
      return;
    }

    await store.dispatch('aiAgents/update', {
      id: agent.id,
      data: { is_active: false },
    });
    useAlert(t('AI_AGENTS.API.DEACTIVATE_SUCCESS'));
  } finally {
    togglingAgentIds.value[agent.id] = false;
  }
};

onMounted(() => {
  store.dispatch('aiAgents/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="t('AI_AGENTS.LIST.LOADING')"
    :no-records-found="false"
  >
    <template #header>
      <div class="pt-4">
        <BaseSettingsHeader
          v-model:search-query="searchQuery"
          :title="t('AI_AGENTS.HEADER')"
          :description="t('AI_AGENTS.DESCRIPTION')"
          :search-placeholder="t('AI_AGENTS.SEARCH_PLACEHOLDER')"
          feature-name="ai_agents"
          bold-title
          compact-toolbar
        >
          <template v-if="aiAgents.length" #count>
            <span class="text-body-main text-n-slate-11">
              {{ t('AI_AGENTS.COUNT', { n: aiAgents.length }) }}
            </span>
          </template>
          <template #actions>
            <div class="flex flex-wrap items-center gap-2">
              <Button
                :label="t('AI_AGENTS.ACTIONS.NEW')"
                icon="i-lucide-plus"
                sm
                class="!text-[15px] !font-medium"
                @click="openCreate"
              />
              <Button
                :label="t('AI_AGENTS.ACTIONS.MANAGE_KNOWLEDGE_SOURCES')"
                icon="i-lucide-book-open"
                outline
                slate
                sm
                class="!text-[15px] !font-medium !bg-n-background"
                @click="openKnowledgeSettings"
              />
            </div>
          </template>
        </BaseSettingsHeader>
      </div>
    </template>

    <template #body>
      <p
        v-if="searchQuery && !filteredAiAgents.length"
        class="flex flex-1 items-center justify-center py-20 text-base text-n-slate-11"
      >
        {{ t('AI_AGENTS.NO_RESULTS') }}
      </p>
      <div
        v-else
        class="grid grid-cols-1 gap-4 pb-6 md:grid-cols-2 lg:grid-cols-4"
      >
        <AiAgentCard
          v-for="agent in filteredAiAgents"
          :key="agent.id"
          :agent="agent"
          :is-toggling="Boolean(togglingAgentIds[agent.id])"
          @edit="openEdit"
          @toggle-active="toggleAgentActive"
          @duplicate="duplicateAgent"
          @delete="openDeleteDialog"
        />
        <AiAgentsCreateCard @create="openCreate" />
      </div>
    </template>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('AI_AGENTS.CONFIRM.DELETE')"
      :is-loading="uiFlags.isDeleting"
      :confirm-button-label="t('AI_AGENTS.ACTIONS.DELETE')"
      :cancel-button-label="t('AI_AGENTS.ACTIONS.CANCEL')"
      @confirm="deleteAgent"
    />
  </SettingsLayout>
</template>
